import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ConfigService } from '../../config/config.service';
import { Arbeitsbereich } from '../../config/model/arbeitsbereich-config.model';
import { LayerContainerService } from './layer-container.service';
import { DetailFormDataModel } from '../../config/model/detail-form-data.model';
import {
  DetailViewFeatureParams,
  RelatedFeatureParams,
} from '../../component/detail-view/detail-view.model';
import * as moment from 'moment';
import 'moment-timezone';
import { Workspace } from '../enum/workspace.enum';
import Enums from '../enum/enums';
import { UrlStateHelperService } from './url-state/url-state-helper.service';
import { GlobalState } from '../../config/model/global-state.model';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class ArbeitsbereichService
 * @version 0.0.1
 * @since November 2017
 * @description The ArbeitsbereichService provides Arbeitsbereiche for usage in components.
 */
@Injectable({
  providedIn: 'root',
})
export class ArbeitsbereichService {
  private readonly arbeitsbereiche: Arbeitsbereich[];
  private _selectedArbeitsbereich: Arbeitsbereich;
  private _arbeitsbereichEditableLyr: __esri.FeatureLayer;
  private _arbeitsbereichMapLyr: __esri.MapImageLayer;
  private _detailFormDataModel: DetailFormDataModel;

  public onChangeArbeitsbereichSubject: Subject<Arbeitsbereich>;

  constructor(
    private _configService: ConfigService,
    private _layerContainerService: LayerContainerService,
    private readonly urlStateHelperService: UrlStateHelperService
  ) {
    // initialisiere alle Arbeitsbereiche aus der Config
    this.arbeitsbereiche = this._configService.arbeitsbereichConfig.arbeitsbereiche;
    // initialisiert Subject wenn Arbeitsbereich gewechselt wird
    this.onChangeArbeitsbereichSubject = new Subject();
  }

  /**
   * @function detailFormDataModel
   * @returns {DetailFormDataModel}
   * @description Getter
   */
  public get detailFormDataModel(): DetailFormDataModel {
    return this._detailFormDataModel;
  }

  /**
   * @function setDetailFormDataModel
   * @description Wenn ein Feature auf der Map geklickt wurde, so werden
   * alle nötigen Informationen über das geklickte Feature hier zusammen gestellt
   * und für die Weiterverarbeitung in ArbeitsBereichService zwischengespeichert.
   * Andere Components können ArbeitsbereichService injecten und auf das selektierte
   * Feature zugreifen.
   * @param featureParams
   */
  public setDetailFormDataModel(featureParams: DetailViewFeatureParams): DetailFormDataModel {
    const respGraphic = featureParams.feature;
    const featureFields = featureParams.layerInfos?.fields;
    let formConfig: any;
    if (
      featureParams.workspace === Workspace.nonWorkspace ||
      featureParams.workspace !== this.selectedArbeitsbereich.id
    ) {
      formConfig = this.getFormConfigForNonWorkspace(featureParams);
    } else {
      formConfig = this.getFormConfig(
        this.selectedArbeitsbereich,
        featureParams.layerId
      );
    }

    if (!formConfig) {
      return null;
    }

    const detailFormDataModel = new DetailFormDataModel();
    if (!featureParams.createMode && respGraphic) {
      this.removeNullStringFromAttributes(respGraphic.attributes);
      this.matchFeatureAttributesType(featureFields, respGraphic.attributes, true);
    } else if (featureParams.createMode) {
      detailFormDataModel.setIsCreateMode(featureParams.createMode);
    }

    detailFormDataModel.setSelectedFeature(respGraphic);
    detailFormDataModel.setSelectedFeatureFields(featureFields);
    detailFormDataModel.setConfigFormFields(formConfig.formFields);
    detailFormDataModel.setFeatureIsEditable(formConfig.editable, featureParams.feature);
    detailFormDataModel.setDetailViewAttributeTitle(formConfig.attributeTitle);
    detailFormDataModel.detailViewTitle = formConfig.detailViewTitle;
    // set config on part features ex. teilparzelle
    if (formConfig.partFeaturesTable && formConfig.partFeaturesTable.columns.length > 0) {
      detailFormDataModel.setPartFeaturesTableConfig(formConfig.partFeaturesTable);
    }

    this._detailFormDataModel = detailFormDataModel;
    // is editable layer exist
    if (formConfig.editableLayerId && formConfig.editableLayerId !== '') {
      // filter editabl featurelayer from layerlist if is exist
      const selectedEditableLyr = this._layerContainerService.layerList.filter(
        f => f.esriLayer.id === formConfig.editableLayerId
      );
      this._arbeitsbereichEditableLyr = selectedEditableLyr[0].esriLayer as __esri.FeatureLayer;
    }

    return this._detailFormDataModel;
  }

  /**
   * @function setRelatedFormDataModel
   * @description Wenn ein Feature in der  related  feature liste  geklickt wurde, so werden
   * alle nötigen Informationen über das geklickte Feature hier zusammen gestellt
   * und für die Weiterverarbeitung in ArbeitsBereichService zwischengespeichert.
   * Andere Components können ArbeitsbereichService injecten und auf das selektierte
   * Feature zugreifen.
   * @param featureParams
   */
  public setRelatedFormDataModel(featureParams: RelatedFeatureParams): DetailFormDataModel {
    const respGraphic = featureParams.feature;
    const featureFields = featureParams.relatedFields;
    const formConfig = featureParams.relatedTableConfig;

    if (!formConfig) {
      return null;
    }

    if (!featureParams.createMode && respGraphic) {
      this.removeNullStringFromAttributes(respGraphic.attributes);
      this.matchFeatureAttributesType(featureFields, respGraphic.attributes, true);
    }

    const detailFormDataModel = new DetailFormDataModel();
    detailFormDataModel.setSelectedFeature(respGraphic);
    detailFormDataModel.setSelectedFeatureFields(featureFields);
    detailFormDataModel.setConfigFormFields(formConfig.formFields);

    this._detailFormDataModel = detailFormDataModel;
    return this._detailFormDataModel;
  }

  /**
   * @function arbeitsBereiche
   * @returns {Arbeitsbereich[]}
   */
  public get arbeitsBereiche(): Arbeitsbereich[] {
    return this.arbeitsbereiche;
  }

  /**
   * @function selectedArbeitsbereich
   * @returns {Arbeitsbereich}
   */
  public get selectedArbeitsbereich(): Arbeitsbereich {
    return this._selectedArbeitsbereich;
  }

  /**
   * @function arbeitsbereichEditableLyr
   * @returns {__esri.FeatureLayer}
   */
  public get arbeitsbereichEditableLyr(): __esri.FeatureLayer {
    return this._arbeitsbereichEditableLyr;
  }

  /**
   * @function arbeitsbereichMapLyr
   * @returns {__esri.MapImageLayer}
   */
  public get arbeitsbereichMapLyr(): __esri.MapImageLayer {
    return this._arbeitsbereichMapLyr;
  }

  /**
   * @function setSelected
   * @param {Arbeitsbereich} item
   * @param navigateTo
   * @param options
   * @description Setze alle nötigen selektieren Arbeitsbereich Daten für weitere Gebrauch
   */
  public setSelected(item: Arbeitsbereich, navigateTo?: boolean, options?: Partial<GlobalState>) {
    if (!item || item.id === this._selectedArbeitsbereich?.id) return;

    this._selectedArbeitsbereich = item;

    if (item.layers && item.layers.length !== 0) {
      const selectedMapLyr = this._layerContainerService.layerList.filter(
        f => f.esriLayer.id === item.layers[0].fkLayer
      )[0];
      this._arbeitsbereichMapLyr = selectedMapLyr
        ? (selectedMapLyr.esriLayer as __esri.MapImageLayer)
        : null;
    }
    if (navigateTo) {
      this.urlStateHelperService.setState(options);
    } else {
      this.urlStateHelperService.setState({
        workspace: this._selectedArbeitsbereich.id as Workspace,
      });
    }
    this.onChangeArbeitsbereichSubject.next(this._selectedArbeitsbereich);
  }

  /* Private methods */

  /**
   * @name removeNullStringFromAttributes
   * @description remove all 'Null'  string values from attributes so that attribute form displayed correctly
   * @param attributes
   */
  private removeNullStringFromAttributes(attributes: any) {
    Object.keys(attributes).forEach(attr => {
      if (attributes[attr] === 'Null') {
        attributes[attr] = null;
      }
    });
  }

  /**
   * @name matchFeatureAttributesType
   * @param featureFields
   * @param featureAttributes
   * @param isRelated
   * @description set  domain codedvalue  value state of  name
   */
  private matchFeatureAttributesType(featureFields, featureAttributes, isRelated: boolean) {
    featureFields.forEach(field => {
      if (featureAttributes[field.alias] != null) {
        if (field.domain) {
          let _domain;
          if (isNaN(featureAttributes[field.alias])) {
            _domain = this.getDomainCodedValue(field, featureAttributes[field.name]);
          } else {
            _domain = this.getDomainName(field, featureAttributes[field.name]);
          }
          featureAttributes[field.name] = _domain[0] ? _domain[0].code : null;
        } else if (
          field.type === 'esriFieldTypeSmallInteger' ||
          field.type === 'esriFieldTypeInteger'
        ) {
          featureAttributes[field.name] = parseInt(featureAttributes[field.name]);
        } else if (field.type === 'esriFieldTypeDate') {
          if (Number.isInteger(featureAttributes[field.name])) {
            const momentDate = moment.utc(new Date(featureAttributes[field.name]), 'DD.MM.YYYY');
            featureAttributes[field.name] = momentDate.toDate();
          }
        }
      }
    });
  }

  /**
   * @name getDomain
   * @param featureField
   * @param attributeCode
   * @description get attribute value from domain codedvalue if is not null
   */
  public getDomain(featureField, attributeCode): any {
    let attributeDomainName = null;
    if (featureField.domain.codedValues.length > 0) {
      attributeDomainName = featureField.domain.codedValues.filter(
        f => f.code === attributeCode || f.name === attributeCode
      );
    }
    return attributeDomainName;
  }

  /**
   * @name getDomainCodedValue
   * @param featureField
   * @param featureAttribute
   * @description get attribute value from domain codedvalue if is not null
   */
  public getDomainCodedValue(featureField, featureAttribute): any {
    let _attributeDomainValue = null;
    if (featureField.domain.codedValues.length > 0) {
      _attributeDomainValue = featureField.domain.codedValues.filter(
        f => f.name === featureAttribute
      );
    }
    return _attributeDomainValue;
  }

  /**
   * @name getDomainName
   * @param featureField
   * @param featureAttribute
   * @description get attribute value from domain codedvalue if is not null
   */
  public getDomainName(featureField, featureAttribute): any {
    let _attributeDomainName = null;
    if (featureField.domain.codedValues.length > 0) {
      _attributeDomainName = featureField.domain.codedValues.filter(
        f => f.code === parseInt(featureAttribute)
      );
    }
    return _attributeDomainName;
  }

  /**
   * @name getFormConfig
   * @param currentArbeitsBereichLyrs
   * @param lyrId
   * @returns configuration on form (form fields)
   * @description get configuration on form from Arbeitsbereich-config
   */
  public getFormConfig(currentArbeitsBereichLyrs, lyrId: number): any {
    // Get all DetailViewForm which are linked to current Arbeitsbereich
    const detailViewsOfCurrentArbeitsbereich = this._configService.detailViewConfig.detailviews.filter(
      dvf => dvf.fkArbeitsbereich === currentArbeitsBereichLyrs.id
    );
    // From detailViewsOfCurrentArbeitsbereich, get only the form specific bind to the identified layer
    const formConfig = detailViewsOfCurrentArbeitsbereich[0].forms.filter(
      form => form.fkIdentifyLayerId === lyrId
    );
    return formConfig[0];
  }

  private getFormConfigForNonWorkspace(featureparams: DetailViewFeatureParams): any {
    const detailViewsConfig = this._configService.detailViewConfig.detailviews.filter(
      dvf =>
        dvf.fkArbeitsbereich === featureparams.workspace &&
        dvf.fkIdentifyLayer === featureparams.fkIdentifyLayer
    )[0];

    if (!detailViewsConfig || !detailViewsConfig.forms) {
      return null;
    }

    return (
      detailViewsConfig.forms.find(form => form.fkIdentifyLayerId === featureparams.layerId) || null
    );
  }
}
